import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import type { LandmarkDef, NpcDef, NpcId } from "./catalog";
import { PLANET_RADIUS, latLonToVector3, orientOnSphere, tangentBasis } from "./sphere";

export type TickFn = (t: number, dt: number) => void;

function mat(color: number, extras: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.75, metalness: 0.08, flatShading: true, ...extras });
}

/** 设定表通用萌脸：两只黑眼 + 腮红 */
function addFace(g: THREE.Group, y: number, z: number, opts: { gap?: number; eye?: number; blush?: boolean; color?: number } = {}): void {
  const gap = opts.gap ?? 0.09;
  const r = opts.eye ?? 0.035;
  const eyeMat = mat(opts.color ?? 0x1f2933, { roughness: 0.4 });
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(r, 8, 8), eyeMat);
  eyeL.position.set(-gap, y, z);
  const eyeR = eyeL.clone();
  eyeR.position.x = gap;
  const hlMat = mat(0xffffff, { emissive: 0xffffff, emissiveIntensity: 0.6 });
  const hlL = new THREE.Mesh(new THREE.SphereGeometry(r * 0.35, 6, 6), hlMat);
  hlL.position.set(-gap + r * 0.35, y + r * 0.35, z + r * 0.7);
  const hlR = hlL.clone();
  hlR.position.x = gap + r * 0.35;
  g.add(eyeL, eyeR, hlL, hlR);
  if (opts.blush !== false) {
    const blushMat = mat(0xf48fb1, { transparent: true, opacity: 0.65 });
    const bl = new THREE.Mesh(new THREE.CircleGeometry(r * 1.1, 8), blushMat);
    bl.position.set(-gap * 1.8, y - r * 1.6, z + 0.005);
    const br = bl.clone();
    br.position.x = gap * 1.8;
    g.add(bl, br);
  }
}

function labelEl(text: string, className: string, kind: string, id: string): CSS2DObject {
  const el = document.createElement("div");
  el.className = className;
  el.textContent = text;
  el.dataset.kind = kind;
  el.dataset.id = id;
  const obj = new CSS2DObject(el);
  obj.position.set(0, 1.55, 0);
  return obj;
}

export function createLandmark(def: LandmarkDef): THREE.Group {
  const group = new THREE.Group();
  group.name = def.id;
  group.userData = { kind: "landmark", id: def.id, url: def.url };
  const building = makeCottage(def.accent, def.roof);
  group.add(building);
  const tag = labelEl(def.name, def.url ? "world-label" : "world-label scenic", "landmark", def.id);
  tag.position.set(0, 2.35, 0);
  group.add(tag);
  group.userData.label = tag.element;
  const smoke = makeSmoke();
  smoke.position.set(0.35, 1.55, -0.15);
  group.add(smoke);
  group.userData.tick = smoke.userData.tick;
  const pos = latLonToVector3(def.lat, def.lon, PLANET_RADIUS);
  orientOnSphere(group, pos, tangentBasis(pos).north);
  return group;
}

export function createNpc(def: NpcDef): THREE.Group {
  const group = new THREE.Group();
  group.name = def.id;
  group.userData = { kind: "npc", id: def.id };
  const figure = makeNpcFigure(def.id);
  group.add(figure);
  group.userData.tick = makeNpcTick(def.id, figure);
  const tag = labelEl(def.name, "world-label npc", "npc", def.id);
  tag.position.set(0, 1.35, 0);
  group.add(tag);
  group.userData.label = tag.element;
  const pos = latLonToVector3(def.lat, def.lon, PLANET_RADIUS);
  orientOnSphere(group, pos, tangentBasis(pos).north);
  return group;
}

/** 各精灵的待机小动作 */
function makeNpcTick(id: NpcId, g: THREE.Group): TickFn {
  const part = (n: string) => g.getObjectByName(n) ?? null;
  const phase = Math.random() * Math.PI * 2;
  switch (id) {
    case "wesight": {
      const thrust = part("thrust");
      return (t) => {
        g.position.y = 0.18 + Math.sin(t * 2.2 + phase) * 0.08;
        if (thrust) thrust.scale.y = 0.8 + Math.abs(Math.sin(t * 14)) * 0.5;
      };
    }
    case "bluebot": {
      const arm = part("armR");
      return (t) => {
        if (arm) arm.rotation.z = -1.6 + Math.sin(t * 5 + phase) * 0.35;
      };
    }
    case "drop": {
      const body = part("body");
      return (t) => {
        const s = 1 + Math.sin(t * 3.4 + phase) * 0.06;
        if (body) body.scale.set(1 / s, 1.25 * s, 1 / s);
      };
    }
    case "flame": {
      const tip = part("tip");
      return (t) => {
        if (tip) {
          tip.scale.set(1 + Math.sin(t * 11 + phase) * 0.12, 1 + Math.sin(t * 9) * 0.2, 1);
          tip.rotation.z = Math.sin(t * 7) * 0.12;
        }
        g.position.y = Math.abs(Math.sin(t * 4 + phase)) * 0.05;
      };
    }
    case "crate": {
      return (t) => {
        g.position.y = Math.abs(Math.sin(t * 3 + phase)) * 0.07;
        g.rotation.y = Math.sin(t * 1.5 + phase) * 0.15;
      };
    }
    case "sprout": {
      const leaf = part("leaf");
      const stem = part("stem");
      return (t) => {
        const sway = Math.sin(t * 2.5 + phase) * 0.25;
        if (stem) stem.rotation.z = sway * 0.5;
        if (leaf) leaf.rotation.z = sway;
      };
    }
    case "blockbot": {
      const head = part("head");
      const eye = part("eye");
      return (t) => {
        const turn = Math.sin(t * 0.9 + phase) * 0.6;
        if (head) head.rotation.y = turn;
        if (eye) eye.rotation.y = turn;
        if (eye) eye.position.set(Math.sin(turn) * 0.18, 1.0, Math.cos(turn) * 0.18);
      };
    }
    case "spacer": {
      const core = part("core") as THREE.Mesh | null;
      return (t) => {
        g.position.y = Math.sin(t * 1.8 + phase) * 0.06;
        if (core) {
          const m = core.material as THREE.MeshStandardMaterial;
          m.emissiveIntensity = 0.6 + Math.sin(t * 4) * 0.4;
        }
      };
    }
    default:
      return () => {};
  }
}

function makeCottage(wall: number, roof: number): THREE.Group {
  const root = new THREE.Group();
  const base = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.95, 1.1), mat(wall));
  base.position.y = 0.48;
  base.castShadow = true;
  const roofMesh = new THREE.Mesh(new THREE.ConeGeometry(1.05, 0.65, 4), mat(roof));
  roofMesh.position.y = 1.2;
  roofMesh.rotation.y = Math.PI / 4;
  roofMesh.castShadow = true;
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.42, 0.06), mat(0x5d4037));
  door.position.set(0, 0.32, 0.56);
  const win = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 0.05), mat(0xfff8e1, { emissive: 0xffe082, emissiveIntensity: 0.35 }));
  win.position.set(0.38, 0.55, 0.56);
  const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.35, 0.16), mat(0x6d4c41));
  chimney.position.set(0.35, 1.35, -0.15);
  // warm glow halo
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 10, 8),
    new THREE.MeshBasicMaterial({ color: 0xffe082, transparent: true, opacity: 0.12 }),
  );
  glow.position.set(0.38, 0.55, 0.6);
  // fence posts
  const fenceMat = mat(0x8d6e63);
  for (let i = -1; i <= 1; i++) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.3, 0.06), fenceMat);
    post.position.set(i * 0.5, 0.15, 0.85);
    root.add(post);
  }
  const rail = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.05, 0.05), fenceMat);
  rail.position.set(0, 0.26, 0.85);
  root.add(rail);
  root.add(base, roofMesh, door, win, chimney, glow);
  return root;
}

/** 烟囱冒烟：4 个小球循环上升淡出 */
function makeSmoke(): THREE.Group {
  const g = new THREE.Group();
  const puffs: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    const m = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.08, 0),
      new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.6, flatShading: true }),
    );
    m.userData.offset = i / 4;
    puffs.push(m);
    g.add(m);
  }
  g.userData.tick = ((t: number) => {
    for (const p of puffs) {
      const k = ((t * 0.25 + p.userData.offset) % 1 + 1) % 1;
      p.position.set(Math.sin(k * 6 + p.userData.offset * 9) * 0.08, k * 0.9, Math.cos(k * 5) * 0.05);
      const s = 0.6 + k * 1.4;
      p.scale.setScalar(s);
      (p.material as THREE.MeshStandardMaterial).opacity = (1 - k) * 0.55;
    }
  }) as TickFn;
  return g;
}

/** 对齐设定表的可读低模 */
export function makeNpcFigure(id: NpcId): THREE.Group {
  switch (id) {
    case "wesight":
      return makeWesight();
    case "bluebot":
      return makeBlueBot();
    case "drop":
      return makeDrop();
    case "flame":
      return makeFlame();
    case "crate":
      return makeCrate();
    case "sprout":
      return makeSprout();
    case "blockbot":
      return makeBlockBot();
    case "spacer":
      return makeSpacer();
    default:
      return makeDrop();
  }
}

function makeWesight(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.32, 12, 10), mat(0x1b4332, { metalness: 0.35 }));
  body.position.y = 0.45;
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.04, 8, 18), mat(0xd4af37, { metalness: 0.5, roughness: 0.4 }));
  ring.position.y = 0.5;
  ring.rotation.x = Math.PI / 2;
  const visor = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), mat(0x0b2b22, { roughness: 0.3 }));
  visor.position.set(0, 0.5, 0.2);
  visor.scale.set(1.2, 0.8, 0.5);
  // smiling LED eyes
  const ledMat = mat(0x9ef01a, { emissive: 0x9ef01a, emissiveIntensity: 1.2 });
  const eyeL = new THREE.Mesh(new THREE.TorusGeometry(0.045, 0.014, 6, 10, Math.PI), ledMat);
  eyeL.position.set(-0.08, 0.5, 0.31);
  const eyeR = eyeL.clone();
  eyeR.position.x = 0.08;
  const scarf = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.07, 6, 12), mat(0xf8f1e3));
  scarf.position.y = 0.26;
  scarf.rotation.x = Math.PI / 2.5;
  const thrust = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.2, 8), mat(0xffd54f, { emissive: 0xff8f00, emissiveIntensity: 0.9 }));
  thrust.name = "thrust";
  thrust.position.y = 0.1;
  thrust.rotation.x = Math.PI;
  g.add(body, ring, visor, eyeL, eyeR, scarf, thrust);
  return g;
}

function makeBlueBot(): THREE.Group {
  const g = new THREE.Group();
  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 0.55, 8), mat(0x3b82f6));
  torso.position.y = 0.55;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.28, 0.28), mat(0x2563eb));
  head.position.y = 1.0;
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 0.16), mat(0x0f172a, { emissive: 0x60a5fa, emissiveIntensity: 0.35 }));
  screen.position.set(0, 1.0, 0.145);
  addFace(g, 1.03, 0.16, { gap: 0.06, eye: 0.025, blush: false, color: 0x93c5fd });
  const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.16, 5), mat(0x94a3b8));
  antenna.position.y = 1.2;
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 6), mat(0xfacc15, { emissive: 0xfacc15, emissiveIntensity: 0.8 }));
  bulb.position.y = 1.29;
  const belt = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.04, 6, 12), mat(0x6d4c41));
  belt.position.y = 0.4;
  belt.rotation.x = Math.PI / 2;
  const armL = new THREE.Mesh(new THREE.CapsuleGeometry(0.04, 0.3, 4, 6), mat(0x60a5fa));
  armL.position.set(-0.26, 0.6, 0);
  const armR = new THREE.Group();
  armR.name = "armR";
  armR.position.set(0.24, 0.78, 0);
  const armRMesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.04, 0.3, 4, 6), mat(0x60a5fa));
  armRMesh.position.y = -0.18;
  const claw = new THREE.Mesh(new THREE.TorusGeometry(0.06, 0.02, 6, 10, Math.PI * 1.4), mat(0x94a3b8));
  claw.position.y = -0.4;
  armR.add(armRMesh, claw);
  armR.rotation.z = -1.6;
  const bootMat = mat(0x1e40af);
  const bootL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.1, 0.2), bootMat);
  bootL.position.set(-0.1, 0.05, 0.03);
  const bootR = bootL.clone();
  bootR.position.x = 0.1;
  g.add(torso, head, screen, antenna, bulb, belt, armL, armR, bootL, bootR);
  return g;
}

function makeDrop(): THREE.Group {
  const g = new THREE.Group();
  const drop = new THREE.Mesh(
    new THREE.SphereGeometry(0.34, 12, 10),
    mat(0x7dd3fc, { transparent: true, opacity: 0.85, metalness: 0.2, roughness: 0.3 }),
  );
  drop.name = "body";
  drop.position.y = 0.42;
  drop.scale.set(1, 1.25, 1);
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.3, 8), mat(0x7dd3fc, { transparent: true, opacity: 0.85, roughness: 0.3 }));
  tip.position.y = 0.95;
  addFace(g, 0.42, 0.31, { gap: 0.1, eye: 0.04 });
  const legL = new THREE.Mesh(new THREE.SphereGeometry(0.08, 6, 6), mat(0x38bdf8));
  legL.position.set(-0.1, 0.08, 0);
  const legR = legL.clone();
  legR.position.x = 0.1;
  g.add(drop, tip, legL, legR);
  return g;
}

function makeFlame(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.36, 10, 8), mat(0xff8a3d, { emissive: 0xff6d00, emissiveIntensity: 0.25 }));
  body.position.y = 0.42;
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.4, 6), mat(0xffe082, { emissive: 0xffb300, emissiveIntensity: 0.5 }));
  tip.name = "tip";
  tip.position.y = 0.88;
  addFace(g, 0.48, 0.33, { gap: 0.1, eye: 0.04 });
  const apron = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.28, 0.08), mat(0x6d2e46));
  apron.position.set(0, 0.32, 0.22);
  const bootL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 0.16), mat(0x4e342e));
  bootL.position.set(-0.1, 0.06, 0.05);
  const bootR = bootL.clone();
  bootR.position.x = 0.1;
  const light = new THREE.PointLight(0xff9800, 0.8, 3);
  light.position.y = 0.6;
  g.add(body, tip, apron, bootL, bootR, light);
  return g;
}

function makeCrate(): THREE.Group {
  const g = new THREE.Group();
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 0.55), mat(0xd7b899));
  box.position.y = 0.4;
  const strap = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.08, 0.58), mat(0x8d6e63));
  strap.position.y = 0.4;
  const strap2 = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.58, 0.08), mat(0x8d6e63));
  strap2.position.y = 0.4;
  addFace(g, 0.5, 0.28, { gap: 0.1, eye: 0.04 });
  const bag = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.22, 0.12), mat(0x66bb6a));
  bag.position.set(0.32, 0.35, 0.1);
  const armMat = mat(0xc4a484);
  const armL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.28, 0.12), armMat);
  armL.position.set(-0.38, 0.4, 0);
  const armR = armL.clone();
  armR.position.x = 0.38;
  const legL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.14), armMat);
  legL.position.set(-0.14, 0.07, 0);
  const legR = legL.clone();
  legR.position.x = 0.14;
  g.add(box, strap, strap2, bag, armL, armR, legL, legR);
  return g;
}

function makeSprout(): THREE.Group {
  const g = new THREE.Group();
  const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.22, 0.32, 8), mat(0xc4784a));
  pot.position.y = 0.2;
  const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.06, 8), mat(0xb5673f));
  rim.position.y = 0.36;
  const cube = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.32, 0.32), mat(0xffe082));
  cube.position.y = 0.52;
  addFace(g, 0.56, 0.165, { gap: 0.08, eye: 0.035 });
  const stem = new THREE.Group();
  stem.name = "stem";
  stem.position.y = 0.68;
  const stemMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.22, 5), mat(0x43a047));
  stemMesh.position.y = 0.11;
  const leaf = new THREE.Group();
  leaf.name = "leaf";
  leaf.position.y = 0.22;
  const leafL = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 6), mat(0x66bb6a));
  leafL.position.x = -0.1;
  leafL.scale.set(1.4, 0.5, 1);
  const leafR = leafL.clone();
  leafR.position.x = 0.1;
  leaf.add(leafL, leafR);
  stem.add(stemMesh, leaf);
  const rootMat = mat(0x8d6e63);
  const rootL = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.08, 0.14), rootMat);
  rootL.position.set(-0.12, 0.04, 0.02);
  const rootR = rootL.clone();
  rootR.position.x = 0.12;
  g.add(pot, rim, cube, stem, rootL, rootR);
  return g;
}

function makeBlockBot(): THREE.Group {
  const g = new THREE.Group();
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.5, 0.4), mat(0x78909c));
  torso.position.y = 0.55;
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.32, 0.32), mat(0x607d8b));
  head.name = "head";
  head.position.y = 0.98;
  const eye = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.08, 0.04), mat(0xce93d8, { emissive: 0xab47bc, emissiveIntensity: 0.9 }));
  eye.name = "eye";
  eye.position.set(0, 1.0, 0.18);
  const jointMat = mat(0x9c27b0, { emissive: 0x7b1fa2, emissiveIntensity: 0.3 });
  const jointL = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.2, 8), jointMat);
  jointL.position.set(0.35, 0.65, 0);
  jointL.rotation.z = Math.PI / 2;
  const jointR = jointL.clone();
  jointR.position.x = -0.35;
  const armMat = mat(0x546e7a);
  const armL = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.45, 0.16), armMat);
  armL.position.set(0.43, 0.42, 0);
  const armR = armL.clone();
  armR.position.x = -0.43;
  const legL = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.3, 0.22), armMat);
  legL.position.set(-0.15, 0.15, 0);
  const legR = legL.clone();
  legR.position.x = 0.15;
  g.add(torso, head, eye, jointL, jointR, armL, armR, legL, legR);
  return g;
}

function makeSpacer(): THREE.Group {
  const g = new THREE.Group();
  const suit = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 0.35, 6, 10), mat(0xf5f5f5));
  suit.position.y = 0.55;
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(0.3, 12, 10),
    mat(0xb3e5fc, { transparent: true, opacity: 0.4, metalness: 0.3, roughness: 0.2 }),
  );
  dome.position.y = 1.05;
  const headBall = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), mat(0x1f2937, { roughness: 0.4 }));
  headBall.position.y = 1.05;
  const ledMat = mat(0x38bdf8, { emissive: 0x38bdf8, emissiveIntensity: 1.3 });
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 6), ledMat);
  eyeL.position.set(-0.07, 1.07, 0.19);
  const eyeR = eyeL.clone();
  eyeR.position.x = 0.07;
  const smile = new THREE.Mesh(new THREE.TorusGeometry(0.05, 0.012, 6, 10, Math.PI), ledMat);
  smile.position.set(0, 0.99, 0.19);
  smile.rotation.z = Math.PI;
  const core = new THREE.Mesh(new THREE.CircleGeometry(0.08, 12), mat(0x38bdf8, { emissive: 0x0284c7, emissiveIntensity: 0.8 }));
  core.name = "core";
  core.position.set(0, 0.55, 0.3);
  const pack = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.35, 0.16), mat(0xe0e0e0));
  pack.position.set(0, 0.6, -0.3);
  const bootMat = mat(0xbdbdbd);
  const bootL = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.1, 0.2), bootMat);
  bootL.position.set(-0.11, 0.05, 0.03);
  const bootR = bootL.clone();
  bootR.position.x = 0.11;
  g.add(suit, dome, headBall, eyeL, eyeR, smile, core, pack, bootL, bootR);
  return g;
}

/** 娜娜式主角：棕发、红围巾、挎包 */
export function makeNanaHero(): THREE.Group {
  const root = new THREE.Group();
  root.name = "nana-hero";

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.38, 6, 10), mat(0x5d4037));
  body.position.y = 0.55;
  body.name = "body";
  const blouse = new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), mat(0xfafafa));
  blouse.position.y = 0.72;
  blouse.scale.set(1, 0.55, 0.9);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 14, 12), mat(0xffe0bd));
  head.position.y = 1.05;
  addFace(root, 1.06, 0.19, { gap: 0.075, eye: 0.03, color: 0x5d4037 });
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.24, 12, 10), mat(0x5d4037));
  hair.position.y = 1.12;
  hair.scale.set(1.05, 0.75, 1.05);
  const bang = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 8), mat(0x5d4037));
  bang.position.set(0, 1.12, 0.16);
  bang.scale.set(1.6, 0.5, 0.6);

  const scarf = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.05, 6, 12), mat(0xc62828));
  scarf.position.y = 0.88;
  scarf.rotation.x = Math.PI / 2.2;

  const bag = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.26, 0.12), mat(0x6d4c41));
  bag.position.set(0.28, 0.5, -0.05);
  const strap = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.025, 4, 12), mat(0x4e342e));
  strap.position.set(0.05, 0.7, 0);
  strap.rotation.z = 0.5;

  const leftLeg = new THREE.Mesh(new THREE.CapsuleGeometry(0.07, 0.22, 4, 8), mat(0x3e2723));
  leftLeg.name = "leftLeg";
  leftLeg.position.set(-0.1, 0.18, 0);
  const rightLeg = leftLeg.clone();
  rightLeg.name = "rightLeg";
  rightLeg.position.x = 0.1;

  const leftArm = new THREE.Mesh(new THREE.CapsuleGeometry(0.055, 0.2, 4, 8), mat(0xffe0bd));
  leftArm.name = "leftArm";
  leftArm.position.set(-0.3, 0.62, 0);
  const rightArm = leftArm.clone();
  rightArm.name = "rightArm";
  rightArm.position.x = 0.3;

  const bootL = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 0.18), mat(0x4e342e));
  bootL.position.set(-0.1, 0.05, 0.02);
  const bootR = bootL.clone();
  bootR.position.x = 0.1;

  root.add(body, blouse, head, hair, bang, scarf, bag, strap, leftLeg, rightLeg, leftArm, rightArm, bootL, bootR);
  return root;
}
